/// <amd-module name="VSS/Features/Frame/ContributedTabProvider" />
import * as React from "react";

import { ObservableArray, IObservableArray, IReadonlyObservableArray, IObservableArrayEventArgs, ObservableLike } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssContributedTab } from "VSSUI/Components/Tabs/Tabs.Props";
import { ContributedContentComponent } from "./ContributedContentComponent";
import { IDisposable } from "VSS/Core/IDisposable";

interface IContributedTabProviderData {
    contributionId?: string;
    provider: IReadonlyObservableArray<IVssContributedTab> & Partial<IDisposable>;
    processProvider: (event: IObservableArrayEventArgs<IVssContributedTab>) => void;
}

interface ITabSpec extends IVssContributedTab {
    /**
     * Optional dependencies that need to be resolved when rendering this component.
     */
    dependencies?: string[];
    /**
     * Contribution id of the content ot display.
     */
    componentType: string;
    /**
     * Props to pass to the component
     */
    componentProperties?: { [prop: string]: any };
}

export interface IContributedTabProviderOptions {
    /**
     * The page context
     */
    pageContext: IVssPageContext;

    /**
     * Find tabs that target this id. Specify exactly one of this or contributionPromise.
     */
    tabGroupIds: string[];

    /**
     * Optional id of the default route to use for tabs that don't specify any route id (e.g. third-party tabs)
     */
    defaultRouteId?: string;

    /**
     * If true, update the URL
     */
    updateUrlOnPivotChange?: boolean;

    /**
     * Additional props to pass to the contributed components.
     */
    props?: { [prop: string]: any };

    /**
     * Route values to use (in conjunction with those on the tab contribution) for determining
     * the url of the tab.
     */
    routeValues?: { [routeParameter: string]: string };
}

export class ContributedTabProvider extends ObservableArray<IVssContributedTab> implements IObservableArray<IVssContributedTab>, IDisposable {
    private pageContext: IVssPageContext;
    private tabGroupIds: string[];
    private defaultRouteId: string | undefined;
    private updateUrlOnPivotChange: boolean | undefined;
    private additionalProps: { [prop: string]: any };
    private routeValues: { [prop: string]: string };
    private providerData: IContributedTabProviderData[];

    constructor(options: IContributedTabProviderOptions) {
        super(undefined);

        this.pageContext = options.pageContext;
        this.tabGroupIds = options.tabGroupIds || [];
        this.defaultRouteId = options.defaultRouteId;
        this.updateUrlOnPivotChange = options.updateUrlOnPivotChange;
        this.additionalProps = options.props || {};
        this.routeValues = options.routeValues || {};
        this.providerData = [];

        this.loadItems();
    }

    public dispose() {
        this.providerData.forEach(providerData => {
            // First unsubscribe to provider
            providerData.provider.unsubscribe(providerData.processProvider);

            // If the provider implements dispose, then call it.
            if (typeof providerData.provider.dispose === "function") {
                providerData.provider.dispose();
            }
        });
    }

    public loadItems(): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        const tabGroupIds = this.tabGroupIds;
        let allProviderIds: string[] = [];
        (tabGroupIds || []).forEach(tabGroupId => {
            allProviderIds = allProviderIds.concat(contributionService.getContributionChildren(tabGroupId, "ms.vss-web.tab-provider") || []);
        });

        const contributionIds = contributionService.getContributionChildren(tabGroupIds[0], "ms.vss-web.tab")!;
        const contributions: (IVssContributedTab | ITabSpec)[] = contributionIds.map(contributionId => {
            const contribution = contributionService.getContribution<ITabSpec>(contributionId)!;
            return contribution.id ? contribution : { ...contribution, id: contributionId };
        });

        contributions.forEach((contribution, index) => {
            if (contribution) {
                const contributionId = contributionIds[index];
                contribution.render = () => {
                    return (
                        <ContributedContentComponent
                            key={contributionId}
                            className="flex-grow"
                            contributionId={contributionId}
                            contribution={contribution}
                            dependencies={(contribution as ITabSpec).dependencies}
                            componentProperties={this.additionalProps}
                        />
                    );
                };
                this.addAdditionalProperties(contribution, contributionId);
            }
        });

        // Keep track of tab providers that need to initialize
        const uninitializedProviders: string[] = [];
        if (allProviderIds && allProviderIds.length > 0) {
            // First try and load synchronously
            for (let childId of allProviderIds) {
                let tabContributionService: IReadonlyObservableArray<IVssContributedTab> | undefined;
                tabContributionService = contributionService.getServiceSync<IReadonlyObservableArray<IVssContributedTab>>(childId, true);

                if (tabContributionService) {
                    this.processContributedService(childId, tabContributionService);

                    // Add initial items to list
                    contributions.push(...tabContributionService.value);
                } else {
                    uninitializedProviders.push(childId);
                }
            }

            // Invoke all async providers
            if (uninitializedProviders.length > 0) {
                for (let providerId of uninitializedProviders) {
                    contributionService
                        .getService<IReadonlyObservableArray<IVssContributedTab>>(providerId)
                        .then((tabContributionService: IReadonlyObservableArray<IVssContributedTab> | undefined) => {
                            if (tabContributionService) {
                                this.processContributedService(providerId, tabContributionService);

                                // Add tabs to obsevable and subscribe for updates
                                this.push(...tabContributionService.value);
                            }
                        });
                }
            }
        }

        this.push(...contributions);
    }

    private processContributedService(providerId: string, tabContributionService: IReadonlyObservableArray<IVssContributedTab>) {
        const providerData = {
            contributionId: providerId,
            provider: tabContributionService,
            processProvider: (event: IObservableArrayEventArgs<IVssContributedTab>) => {
                if (event.addedItems) {
                    for (let tab of event.addedItems) {
                        this.addAdditionalProperties(tab, providerId!);
                    }

                    this.push(...event.addedItems);
                }
            }
        };

        // Process each tab
        for (let tab of tabContributionService.value) {
            this.addAdditionalProperties(tab, providerId!);
        }

        // Subscribe to updates
        providerData.provider.subscribe(providerData.processProvider);

        // Keep track of the provider
        this.providerData.push(providerData);
    }

    private addAdditionalProperties(tab: IVssContributedTab, contributionId: string) {
        if (tab.routeId) {
            const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");
            tab.url = locationService.routeUrl(tab.routeId, { ...this.routeValues, ...tab.routeValues });
        } else if (this.defaultRouteId) {
            const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");
            tab.url = locationService.routeUrl(this.defaultRouteId, { contributionId });
        }

        if (tab.fpsRequired) {
            tab.onBeforeTabChange = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | null, tabId: string, url?: string) => {
                if (url) {
                    FastPageSwitch(this.pageContext, url, true);
                    ev && ev.preventDefault();
                    return false;
                } else {
                    return true;
                }
            };
        } else if (tab.url && this.updateUrlOnPivotChange) {
            tab.onBeforeTabChange = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | null, tabId: string, url?: string) => {
                const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
                historyService.pushState(undefined, tab.url && ObservableLike.getValue(tab.url));
                return true;
            };
        }
    }
}
